import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Bug } from 'lucide-react';
import { Spinner } from '../design-system';
import { useAuth } from '../contexts/AuthContext';
import { captureFeedback } from '../config/sentry';
import toast from '../design-system/toast';
import { apiEndpoints } from '../utils/api';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';

// Extend Window interface for TypeScript
declare global {
  interface Window {
    recentErrors: any[];
  }
}

// Global error tracking
if (typeof window !== 'undefined' && !window.recentErrors) {
  window.recentErrors = [];
  
  // Capture console errors
  const originalError = console.error;
  console.error = function(...args) {
    window.recentErrors.push({
      type: 'error',
      message: args.join(' '),
      timestamp: new Date().toISOString(),
      stack: new Error().stack
    });
    // Keep only last 10 errors
    if (window.recentErrors.length > 10) {
      window.recentErrors = window.recentErrors.slice(-10);
    }
    originalError.apply(console, args);
  };
  
  // Capture unhandled errors
  window.addEventListener('error', (event) => {
    window.recentErrors.push({
      type: 'unhandled',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString(),
      stack: event.error?.stack
    });
    if (window.recentErrors.length > 10) {
      window.recentErrors = window.recentErrors.slice(-10);
    }
  });
  
  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    window.recentErrors.push({
      type: 'promise_rejection',
      message: event.reason?.message || String(event.reason),
      timestamp: new Date().toISOString(),
      stack: event.reason?.stack
    });
    if (window.recentErrors.length > 10) {
      window.recentErrors = window.recentErrors.slice(-10);
    }
  });
}

// Base modal wrapper with backdrop blur and smooth animations.
// Rendered into a portal on document.body so the overlay escapes the header's
// stacking context (the header is `sticky z-30`); otherwise the z-40 collapsed
// sidebar would paint above the overlay and stay unblurred.
const ModalWrapper = ({ children, isOpen }: { children: React.ReactNode; isOpen: boolean }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop with blur */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px]" />

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        {children}
      </div>
    </div>,
    document.body,
  );
};

interface BugReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const BugReportDialog: React.FC<BugReportDialogProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const authenticatedApi = useAuthenticatedApi();
  const [formData, setFormData] = useState({
    description: '',
    issueType: 'bug'
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<{file: File, preview?: string}[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const issueTypes = [
    { value: 'bug', label: 'Bug/Error' },
    { value: 'ui_issue', label: 'UI/Design Issue' },
    { value: 'performance', label: 'Performance Issue' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'security', label: 'Security Concern' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Filter valid files (max 10MB each)
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });
    
    // Limit total files to 5
    const totalFiles = selectedFiles.length + validFiles.length;
    if (totalFiles > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
    
    // Create previews for new files
    validFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews(prev => [
            ...prev,
            { file, preview: e.target?.result as string }
          ]);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreviews(prev => [...prev, { file }]);
      }
    });
  };

  const removeFile = (fileToRemove: File) => {
    setSelectedFiles(prev => prev.filter(file => file !== fileToRemove));
    setFilePreviews(prev => prev.filter(item => item.file !== fileToRemove));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const removeAllFiles = () => {
    setSelectedFiles([]);
    setFilePreviews([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const captureConsoleInfo = () => {
    const consoleInfo = {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height
      },
      consoleErrors: [] as any[]
    };
    
    // Capture recent console errors and memory info if available
    try {
      if (window.console && (window.console as any).memory) {
        (consoleInfo as any).memoryUsage = (window.console as any).memory;
      }
      
      if (window.recentErrors && Array.isArray(window.recentErrors)) {
        consoleInfo.consoleErrors = window.recentErrors.slice(-5);
      }
    } catch {
      // Silently ignore if not available
    }
    
    return consoleInfo;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const systemInfo = captureConsoleInfo();
      
      const userName = user?.name || user?.email || '';
      const userEmail = user?.email || '';
      
      // Process attachments if any
      const attachments = selectedFiles.length > 0 ? await Promise.all(
        selectedFiles.map(async (file, index) => {
          const arrayBuffer = await file.arrayBuffer();
          return {
            filename: file.name || `attachment_${index + 1}`,
            data: new Uint8Array(arrayBuffer),
            contentType: file.type || 'application/octet-stream',
          };
        })
      ) : undefined;

      const feedbackData = {
        name: `Feedback from ${userName} (${userEmail}): Issue Type: ${formData.issueType}`,
        email: userEmail,
        message: `Description: ${formData.description}\n\nSystem Info: ${JSON.stringify(systemInfo, null, 2)}${selectedFiles.length > 0 ? `\n\nAttached Files: ${selectedFiles.map(f => f.name).join(', ')}` : ''}`,
        tags: {
          source: 'custom_dialog',
          issue_type: formData.issueType,
          has_attachments: selectedFiles.length > 0 ? 'true' : 'false',
          attachment_count: selectedFiles.length.toString(),
        },
        ...(attachments && { attachments })
      };

      // Capture feedback to Sentry first and get the event ID
      const sentryEventId = captureFeedback(feedbackData);

      // Also submit to our internal support request system
      try {
        await authenticatedApi.post(
          apiEndpoints.supportRequests.create,
          {
            description: formData.description,
            category: formData.issueType,
            sentry_feedback_id: sentryEventId,
          }
        );
      } catch (apiError) {
        // Log error but don't fail the submission - Sentry feedback is already captured
        console.error('Failed to submit to internal support system:', apiError);
      }
      
      // Show success toast and close dialog
      toast.success('Bug report submitted successfully! Thank you for your feedback.');
      onClose();
      setFormData({ description: '', issueType: 'bug' });
      removeAllFiles();
    } catch (error) {
      // Silent error handling - Sentry will capture the error
      toast.error('Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setFormData({ description: '', issueType: 'bug' });
    removeAllFiles();
  };

  return (
    <ModalWrapper isOpen={isOpen}>
      <div className="relative bg-ui-background-secondary rounded-2xl shadow-2xl w-full max-w-2xl mx-auto overflow-hidden transform transition-all">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />
        
        {/* Header with subtle background */}
        <div className="relative flex items-center justify-between px-6 py-3 bg-ui-background-tertiary border-b border-ui-border-primary">
          <h2 className="text-base font-semibold text-ui-text-primary">
            Report a Bug
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-ui-background-tertiary transition-colors"
          >
            <X size={20} className="text-ui-text-tertiary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="relative p-6">
          <div className="space-y-5">

                {/* Issue Type */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-ui-text-secondary" htmlFor="issueType">
                    Issue Type
                  </label>
                  <select
                    id="issueType"
                    name="issueType"
                    value={formData.issueType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-ui-background-tertiary border border-ui-border-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-action focus:border-transparent transition-all text-ui-text-primary"
                    data-tour="bug-report-issue-type"
                  >
                    {issueTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-ui-text-secondary" htmlFor="description">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Please describe the issue you encountered. Include steps to reproduce if possible..."
                    rows={6}
                    className="w-full px-4 py-3 bg-ui-background-tertiary border border-ui-border-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-action focus:border-transparent transition-all text-ui-text-primary placeholder-ui-text-tertiary resize-none"
                    data-tour="bug-report-description"
                    required
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-ui-text-secondary">
                    Attachments (Optional) - Max 5 files, 10MB each (file names will be included in report)
                  </label>
                  <div className="space-y-3">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center px-4 py-3 bg-ui-background-tertiary border border-ui-border-primary rounded-xl hover:bg-ui-background-tertiary transition-all text-ui-text-secondary"
                      data-tour="bug-report-attachments"
                      disabled={selectedFiles.length >= 5}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {selectedFiles.length === 0 ? 'Upload Files' : `Add More Files (${selectedFiles.length}/5)`}
                    </button>
                  
                    {selectedFiles.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-ui-text-secondary">
                            {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                          </span>
                          <button
                            type="button"
                            onClick={removeAllFiles}
                            className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Remove All
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto overflow-x-hidden">
                          {filePreviews.map((item, index) => (
                            <div key={index} className="relative group">
                              <div className="aspect-square bg-ui-background-tertiary rounded border border-ui-border-primary overflow-hidden w-24 h-24 mx-auto">
                                {item.preview ? (
                                  <img
                                    src={item.preview}
                                    alt={item.file.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-ui-text-tertiary" />
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(item.file)}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                              <div className="mt-1 text-center">
                                <p className="text-xs text-ui-text-secondary truncate text-center" title={item.file.name}>
                                  {item.file.name.length > 12 ? `${item.file.name.substring(0, 12)}...` : item.file.name}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-8">
            <button
              type="button"
              className="px-4 py-2 text-sm text-ui-text-secondary bg-slate-300 dark:bg-ui-background-tertiary hover:bg-ui-background-tertiary rounded-xl font-medium transition-all"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-brand-action hover:bg-brand-action-hover text-brand-action-text rounded-xl font-medium transition-all flex items-center disabled:opacity-50 disabled:bg-slate-300 dark:disabled:bg-neutral-600 disabled:text-ui-text-tertiary dark:disabled:text-ui-text-tertiary shadow-sm"
              data-tour="bug-report-submit"
              disabled={isSubmitting || !formData.description.trim()}
            >
              {isSubmitting ? (
                <>
                  <Spinner size={16} tone="current" className="mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Bug size={16} className="mr-2" />
                  Submit Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalWrapper>
  );
};

export default BugReportDialog;
